/** @file facet.setTable: gives one entry another sector table (built-in or user) and its drawn angles. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { SECTOR_TABLES } from '@zilo/pose3d-format/tables/sectorTables';
import type { SectorTable } from '@zilo/pose3d-format/types/facet';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { checkArray } from '@zilo/pose3d-format/validate/fields/checkArray';
import { checkNumber } from '@zilo/pose3d-format/validate/fields/checkNumber';
import { validateSectorTable } from '@zilo/pose3d-format/validate/validateSectorTable';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { puppetInverse } from '../apply/puppetInverse';
import type { CommandContext } from '../context/commandContext';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { entryInverse } from './entryInverse';
import { findEntry } from './findEntry';
import { requireSectorTable } from './requireSectorTable';
import { retargetEntry } from './retargetEntry';

const angles = checkArray(checkNumber());
const ANGLE_KEYS = ['drawnAzimuths', 'drawnScreenAngles'] as const;

/** The table to use and whether it must be stored in `puppet.sectorTables` (a new user table). */
function resolve(p: Puppet, table: string | SectorTable, ctx: CommandContext): { table: SectorTable; store: boolean } {
  if (typeof table === 'string') return { table: requireSectorTable(p, table, ctx.sectorTables), store: false };
  if (Object.hasOwn(SECTOR_TABLES, table.id) || Object.hasOwn(ctx.sectorTables, table.id)) {
    throwError(ERROR_CODES.VALIDATION, { table: table.id }, { path: '/input/table/id', hint: 'a user table id must not be a built-in id' });
  }
  const stored = p.sectorTables?.[table.id];
  if (stored !== undefined && JSON.stringify(stored) !== JSON.stringify(table)) {
    throwError(ERROR_CODES.VALIDATION, { table: table.id }, { path: '/input/table/id', hint: 'another table has this id; pick a new id or pass the id string' });
  }
  return { table, store: stored === undefined };
}

/**
 * `facet.setTable { drawable, entry, table, drawnAzimuths?, drawnScreenAngles? }` → `{ tiles }`: a table object is
 * stored in `puppet.sectorTables`; `retargetEntry` moves the entry (drawn angles follow, slots of vanished tiles are
 * dropped). Inverse E, or P for a new table.
 */
export const facetSetTableCommand: CommandDef<'facet.setTable'> = {
  name: 'facet.setTable',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['drawable', 'entry', 'table', ...ANGLE_KEYS]);
    const table = typeof r.raw('table') === 'string' ? r.str('table') : validateSectorTable(r.raw('table'), r.at('table'));
    const read = (k: string): number[] => r.check(k, angles) as number[];
    return compact({ drawable: r.str('drawable'), entry: r.int('entry', { min: 0 }), table, drawnAzimuths: r.opt('drawnAzimuths', read), drawnScreenAngles: r.opt('drawnScreenAngles', read) });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const p = draft.puppet;
    const { entry } = findEntry(p, input.drawable, input.entry);
    const { table, store } = resolve(p, input.table, ctx);
    const inverse = store ? puppetInverse(draft) : entryInverse(input.drawable, input.entry, entry);
    const tiles = retargetEntry(entry, table, input);
    if (store) p.sectorTables = { ...p.sectorTables, [table.id]: structuredClone(table) };
    checkPuppet(draft, ctx);
    return { result: { tiles }, warnings: [], inverse };
  },
};
